import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ModalController, AlertController, IonContent, IonSearchbar } from '@ionic/angular';
import { Trabajo } from '../../models/trabajo';
import { JavapiService } from '../../services/javapi.service';
import { MetodosgenService } from '../../services/metodosgen.service';
import { Metodosui } from '../../services/metodosui';
import { firstValueFrom } from 'rxjs';
import { LlenardatosComponent } from '../llenardatos/llenardatos.component';
import { FormModel } from '../../models/dynamic-form';

@Component({
  selector: 'app-worker-selector',
  templateUrl: './worker-selector.component.html',
  styleUrls: ['./worker-selector.component.scss'],
  standalone: false
})
export class WorkerSelectorComponent implements OnInit {
  @ViewChild(IonContent) content!: IonContent;
  @ViewChild('searchBar') searchBar!: IonSearchbar;

  @Input() trabajo: Trabajo | null = null;
  @Input() date: string = '';
  @Input() existingIds: any[] = []; // IDs of workers already added
  @Input() existingWorkers: any[] = []; // Workers with their shift already added
  @Input() mode: 'planilla' | 'select' = 'planilla';

  searchTerm: string = '';
  workers: any[] = [];
  filteredWorkers: any[] = [];
  loading: boolean = false;

  // Pagination
  ITEMS_PER_PAGE: number = 20;
  displayedWorkers: any[] = [];
  roles: any[] = [];

  constructor(
    private modalCtrl: ModalController,
    private javapiService: JavapiService,
    private metodosGenService: MetodosgenService,
    private metodosUi: Metodosui,
    private alertCtrl: AlertController
  ) { }

  async ngOnInit() {
    await this.loadRoles();
    await this.loadWorkers();

    // safe guard if inputs are undefined
    if (!this.existingIds) this.existingIds = [];
    if (!this.existingWorkers) this.existingWorkers = [];
  }

  isAlreadyAdded(workerId: any): boolean {
    // Si no tenemos la lista con turnos, validamos solo por ID
    if (!this.existingWorkers || this.existingWorkers.length === 0) {
      return this.existingIds.some(id => String(id) === String(workerId));
    }
    // Si ya existe en ambos turnos (Día y Noche) en este lugar, lo deshabilitamos de la lista principal
    const turnosGuardados = this.existingWorkers.filter(w => String(w.trabajador_id) === String(workerId));
    return turnosGuardados.length >= 2;
  }

  close(saved: boolean = false) {
    this.modalCtrl.dismiss({ saved });
  }

  async loadRoles() {
    try {
      const res: any = await firstValueFrom(this.javapiService.executeAdminCrud('ROL', 'SELECT_ALL', { dt1: '' }));
      if (Array.isArray(res)) {
        this.roles = res;
      }
    } catch (e) {
      console.error('Error loading roles', e);
    }
  }

  async loadWorkers() {
    this.loading = true;
    try {
      const user = localStorage.getItem('user_name') || 'system';
      const data: any = await firstValueFrom(
        this.javapiService.getTrabajadoresList(this.searchTerm, this.trabajo?.id || '', user)
      );

      this.workers = [];
      if (Array.isArray(data)) {
        this.workers = data.map(d => {
          const costoDia = Number(d.dt11) || 0;
          const costoNoche = Number(d.dt12) || 0;
          return {
            id: d.dt1,
            cedula: d.dt2,
            nombre: d.dt3,
            alias: d.dt4,
            estado: d.dt5,
            correo: d.dt6,
            direccion: d.dt7,
            telefono: d.dt8,
            id_rol: d.dt13, // ID numérico del rol
            rol: d.dt9 || 'Sin rol', // Nombre del rol que viene del SP
            costo_dia: costoDia > 0 ? costoDia : 35,
            costo_noche: costoNoche > 0 ? costoNoche : 40
          };
        });

        this.resolveRoleNames();
        this.filterWorkers();
      }
    } catch (e) {
      console.error('Error loading workers', e);
    } finally {
      this.loading = false;
    }
  }

  resolveRoleNames() {
    if (this.roles.length === 0) return;
    this.workers.forEach(w => {
      // Intentamos buscar por ID (dt1 en el CRUD de ROL)
      const role = this.roles.find(r => String(r.dt1) === String(w.id_rol));
      if (role) {
        w.rol = role.dt2; // Nombre del rol
        // Si el trabajador NO traía costos específicos del backend, usamos los del rol
        if (w.costo_dia === 35 && Number(role.dt3) > 0) w.costo_dia = Number(role.dt3);
        if (w.costo_noche === 40 && Number(role.dt4) > 0) w.costo_noche = Number(role.dt4);
      }
    });
  }

  filterWorkers() {
    if (!this.searchTerm) {
      this.filteredWorkers = this.workers;
    } else {
      const term = this.searchTerm.toLowerCase();
      this.filteredWorkers = this.workers.filter(w =>
        w.nombre.toLowerCase().includes(term) ||
        (w.alias && w.alias.toLowerCase().includes(term))
      );
    }

    // Reset and load first page
    this.displayedWorkers = [];
    this.addMoreItems();
  }

  addMoreItems(event?: any) {
    const nextSet = this.filteredWorkers.slice(
      this.displayedWorkers.length,
      this.displayedWorkers.length + this.ITEMS_PER_PAGE
    );

    this.displayedWorkers.push(...nextSet);

    if (event) {
      event.target.complete();
      if (this.displayedWorkers.length >= this.filteredWorkers.length) {
        event.target.disabled = true;
      }
    }
  }

  loadData(event: any) {
    this.addMoreItems(event);
  }

  async createNewWorker() {
    // Form for new worker - conforming to InputItem interface
    const inputItems: any[] = [
      {
        nombre: 'Nombre Completo',
        type: 'text',
        value: '',
        ngmNumber: '1',
        size: '12',
        position: 'floating',
        readonly: false
      },
      {
        nombre: 'Cédula',
        type: 'text',
        value: '',
        ngmNumber: '2',
        size: '12',
        position: 'floating',
        readonly: false
      },
      {
        nombre: 'Alias (Opcional)',
        type: 'text',
        value: '',
        ngmNumber: '3',
        size: '12',
        position: 'floating',
        readonly: false
      },
      {
        nombre: 'Rol / Cargo',
        type: 'text',
        value: '',
        ngmNumber: '4',
        size: '12',
        position: 'floating',
        readonly: false,
        subrutina: 'listTrRoles'
      },
      {
        nombre: 'Teléfono',
        type: 'tel',
        value: '',
        ngmNumber: '5',
        size: '12',
        position: 'floating',
        readonly: false
      }
    ];

    const model: FormModel = {
      mostrar: { titulo: 'Nuevo Trabajador', color: 'primary' },
      datos: {},
      components: {
        input: inputItems,
        button: [
          {
            nombre: 'Guardar Trabajador',
            color: 'primary',
            action: 'guardar'
          }
        ]
      }
    };

    const savedData = await this.metodosUi.openModal(LlenardatosComponent, { model });

    if (savedData && savedData.action === 'guardar') {
      const comps = savedData.data.components.input;
      const getVal = (n: string) => comps.find((c: any) => c.ngmNumber == n)?.value || '';

      // SP: sp_lista_trabajadores_crud(p_accion, p_id, p_cedula, p_nombre, p_alias, p_estado, p_correo, p_direccion, p_telefono, p_rol)
      // Backend: action=subrutina, p_id=dt1, p_cedula=dt2, p_nombre=dt3, p_alias=dt4, p_estado=dt5, p_correo=dt6, p_direccion=dt7, p_telefono=dt8, p_rol=dt9
      const spPayload = this.metodosGenService.setModeloGeneral(
        'trabajadores',    // rutina (ignored by endpoint)
        'INSERT',          // subrutina → action
        '',                // dt1 = p_id (empty = 0 for new worker)
        getVal('2'),       // dt2 = p_cedula
        getVal('1'),       // dt3 = p_nombre
        getVal('3'),       // dt4 = p_alias
        'V',               // dt5 = p_estado (active)
        '',                // dt6 = p_correo
        '',                // dt7 = p_direccion
        getVal('5'),       // dt8 = p_telefono
        getVal('4'),       // dt9 = p_rol
        '', '', '', '', '', ''
      );

      try {
        const res = await firstValueFrom(this.javapiService.saveTrabajador(spPayload));
        if (res && res.success) {
          this.metodosUi.showToast('Trabajador creado', 'success');
          this.loadWorkers();
        } else {
          this.metodosUi.showToast('Error al crear trabajador', 'danger');
        }
      } catch (e) {
        console.error(e);
        this.metodosUi.showToast('Error al guardar', 'danger');
      }
    }
  }

  // Batch mode
  selectedWorkers: Set<any> = new Set();
  isReviewMode: boolean = false;
  selectedShift: string = 'D';
  reviewList: any[] = [];
  displayCostoDia: number = 35;
  displayCostoNoche: number = 40;

  toggleSelection(worker: any) {
    if (this.isAlreadyAdded(worker.id)) return; // Prevent selecting if already added

    if (this.selectedWorkers.has(worker.id)) {
      this.selectedWorkers.delete(worker.id);
    } else {
      this.selectedWorkers.add(worker.id);
    }
  }

  startReview() {
    if (this.selectedWorkers.size === 0) return;
    // Default to Day 'D' initially
    this.selectedShift = 'D';
    this.prepareReviewList('D');

    // Scroll to top to show header and shift selector
    // Wait for DOM to update with Review Mode view
    setTimeout(() => {
      if (this.content) {
        this.content.scrollToTop(500); // 500ms duration
      }
    }, 300); // Wait 300ms for view switch
  }

  updateShift(newShift: string) {
    this.selectedShift = newShift;

    // Update all items in the list based on their specific role cost
    this.reviewList.forEach(item => {
      item.turno = newShift;
      item.monto = newShift === 'D' ? item.costo_dia : item.costo_noche;
    });
  }

  prepareReviewList(turno: string) {
    this.reviewList = this.workers
      .filter(w => this.selectedWorkers.has(w.id))
      .map(w => ({
        ...w,
        turno: turno,
        monto: turno === 'D' ? w.costo_dia : w.costo_noche,
        extra: null,
        descuento: null,
        subcontrato: false, // Default to NO subcontrato
        nota: ''
      }));

    // Update display prices for the shift cards based on the first selected worker
    if (this.reviewList.length > 0) {
      this.displayCostoDia = this.reviewList[0].costo_dia;
      this.displayCostoNoche = this.reviewList[0].costo_noche;
    }

    this.isReviewMode = true;
  }

  async saveBatch() {
    // 1. Validar duplicados de turno antes de guardar cualquier registro
    for (const item of this.reviewList) {
      const yaExiste = this.existingWorkers.some(
        w => String(w.trabajador_id) === String(item.id) && String(w.turno) === String(item.turno)
      );

      if (yaExiste) {
        const turnoTxt = item.turno === 'D' ? 'Diurno' : 'Nocturno';
        await this.metodosUi.showAlert(
          'Duplicado Detectado',
          `El trabajador ${item.nombre} ya tiene registrado planilla para este día en el turno ${turnoTxt}.`
        );
        return; // Detener todo el proceso de guardado
      }
    }

    this.loading = true;
    let savedCount = 0;
    let errors = 0;

    for (const item of this.reviewList) {
      try {
        const spPayload = this.metodosGenService.setModeloGeneral(
          "Planilla", "INSERT", "0", String(this.trabajo?.id), String(item.id),
          this.date,
          item.turno,
          String(item.monto),
          String(item.extra || 0),
          String(item.descuento || 0),
          item.nota || "",
          item.subcontrato ? 'S' : 'N', // dt10 = subcontrato
          "", "", "", "", ""
        );

        const res: any = await firstValueFrom(this.javapiService.savePlanillaActividad(spPayload));
        if (res && res.success) {
          savedCount++;
        } else {
          errors++;
        }
      } catch (e) {
        console.error(e);
        errors++;
      }
    }

    this.loading = false;
    this.metodosUi.showToast(`Guardados: ${savedCount}. Errores: ${errors}`, errors > 0 ? 'warning' : 'success');

    if (savedCount > 0) {
      this.close(true);
    }
  }

  cancelReview() {
    this.isReviewMode = false;
    this.reviewList = [];
  }

  isSelected(worker: any): boolean {
    return this.selectedWorkers.has(worker.id);
  }

  confirmSelection() {
    const selectedList = this.workers.filter(w => this.selectedWorkers.has(w.id));
    this.modalCtrl.dismiss({ saved: true, workers: selectedList });
  }
}
